/**
 * Super Admin — Imports & Migrations report page.
 *
 * Tabbed view of:
 *   1. Schema migration status (Production + Dev/Test environments)
 *   2. Import job history (read-only log of past jobs)
 *   3. Email Recovery wizard (reconstruct lost bookings/leads from Gmail)
 *
 * Interactive import workflow lives at /platform/ai-import.
 */
import { useQuery } from "@tanstack/react-query";
import {
  Database,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  Loader2,
  FileWarning,
  Upload,
  Mail,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlatformLayout } from "./platform-layout";
import { Link } from "wouter";
import EmailRecoveryWizard from "@/pages/EmailRecoveryWizard";

// ─── Migration types (from platform-migrations) ───────────────────────────────

interface AppliedMigrationRow {
  filename: string;
  appliedAt: string;
}

interface MigrationFileEntry {
  filename: string;
  inRepo: boolean;
  applied: boolean;
  appliedAt: string | null;
  contentSha256: string | null;
  contentMatches: boolean | null;
}

interface TrackerSummary {
  schema: string;
  table: string;
  available: boolean;
  applied: AppliedMigrationRow[];
  files: MigrationFileEntry[];
  pendingCount: number;
  orphanCount: number;
  driftedCount: number;
}

interface PlatformMigrationsResponse {
  repoFiles: string[];
  production: TrackerSummary;
  test: TrackerSummary;
}

// ─── Import job types (from platform-onboarding-imports) ──────────────────────

interface ImportJob {
  id: string;
  sourcePlatform: string;
  sourceLabel: string | null;
  targetWorkspaceId: string;
  status: "draft" | "ingesting" | "mapping" | "dry_run" | "committing" | "committed" | "aborted";
  operatorUserId: string;
  notes: string | null;
  createdAt: string;
  committedAt: string | null;
  abortedAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusFor(entry: MigrationFileEntry): {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  icon: typeof CheckCircle2;
} {
  if (entry.applied && entry.inRepo) {
    if (entry.contentMatches === false) {
      return { label: "Drifted", variant: "destructive", icon: FileWarning };
    }
    if (entry.contentMatches === null) {
      return { label: "Unknown content", variant: "outline", icon: HelpCircle };
    }
    return { label: "Applied", variant: "default", icon: CheckCircle2 };
  }
  if (!entry.applied && entry.inRepo) {
    return { label: "Pending", variant: "secondary", icon: AlertTriangle };
  }
  return { label: "Orphan", variant: "destructive", icon: HelpCircle };
}

const STATUS_COLORS: Record<string, string> = {
  draft: "bg-slate-500",
  ingesting: "bg-amber-500",
  mapping: "bg-blue-500",
  dry_run: "bg-indigo-500",
  committing: "bg-purple-500",
  committed: "bg-emerald-600",
  aborted: "bg-rose-600",
};

// ─── Migration tracker card ───────────────────────────────────────────────────

function TrackerCard({
  title,
  description,
  tracker,
  testidPrefix,
}: {
  title: string;
  description: string;
  tracker: TrackerSummary;
  testidPrefix: string;
}) {
  return (
    <Card data-testid={`card-${testidPrefix}`}>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              {title}
            </CardTitle>
            <CardDescription>
              {description}
              <span className="ml-2 font-mono text-xs">{tracker.table}</span>
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            {tracker.driftedCount > 0 && (
              <Badge variant="destructive" data-testid={`badge-${testidPrefix}-drifted`}>
                {tracker.driftedCount} drifted
              </Badge>
            )}
            {tracker.pendingCount > 0 && (
              <Badge variant="secondary" data-testid={`badge-${testidPrefix}-pending`}>
                {tracker.pendingCount} pending
              </Badge>
            )}
            {tracker.orphanCount > 0 && (
              <Badge variant="destructive" data-testid={`badge-${testidPrefix}-orphan`}>
                {tracker.orphanCount} orphan
              </Badge>
            )}
            {tracker.pendingCount === 0 && tracker.orphanCount === 0 && tracker.driftedCount === 0 && (
              <Badge variant="default" data-testid={`badge-${testidPrefix}-clean`}>
                In sync
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!tracker.available && (
          <div
            className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
            data-testid={`text-${testidPrefix}-unavailable`}
          >
            <AlertTriangle className="h-5 w-5" />
            <span className="text-sm">
              Tracker table not present yet. Repo files are listed below as
              pending; once the runner has applied them at least once, the
              tracker will appear here.
            </span>
          </div>
        )}
        {tracker.files.length === 0 ? (
          <div
            className="py-6 text-center text-sm text-muted-foreground"
            data-testid={`text-${testidPrefix}-empty`}
          >
            No migrations to display.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid={`table-${testidPrefix}`}>
              <thead>
                <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">Filename</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Applied at</th>
                </tr>
              </thead>
              <tbody>
                {tracker.files.map((entry) => {
                  const status = statusFor(entry);
                  const Icon = status.icon;
                  return (
                    <tr
                      key={entry.filename}
                      className="border-b last:border-0"
                      data-testid={`row-${testidPrefix}-${entry.filename}`}
                    >
                      <td className="py-2 pr-4 font-mono text-xs">{entry.filename}</td>
                      <td className="py-2 pr-4">
                        <Badge variant={status.variant} className="gap-1" data-testid={`status-${testidPrefix}-${entry.filename}`}>
                          <Icon className="h-3 w-3" />
                          {status.label}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4 text-muted-foreground" data-testid={`text-${testidPrefix}-applied-at-${entry.filename}`}>
                        {formatDate(entry.appliedAt)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlatformImportsMigrations() {
  const {
    data: migrationsData,
    isLoading: migrationsLoading,
    isError: migrationsError,
    refetch: refetchMigrations,
    isFetching: migrationsFetching,
  } = useQuery<PlatformMigrationsResponse>({
    queryKey: ["/api/platform/migrations"],
  });

  const { data: jobs, isLoading: jobsLoading } = useQuery<ImportJob[]>({
    queryKey: ["/api/platform/onboarding/jobs"],
  });

  return (
    <PlatformLayout>
      <div className="p-3 sm:p-6 space-y-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Imports &amp; Migrations</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Schema migration status, import job history, and email recovery tools.
            To run a new import, use{" "}
            <Link href="/platform/ai-import" className="text-primary underline underline-offset-2">
              AI Import
            </Link>
            .
          </p>
        </div>

        <Tabs defaultValue="schema" className="w-full">
          <TabsList className="mb-4" data-testid="tabs-imports-migrations">
            <TabsTrigger value="schema" className="gap-1.5" data-testid="tab-schema-migrations">
              <Database className="h-4 w-4" />
              Schema Migrations
            </TabsTrigger>
            <TabsTrigger value="imports" className="gap-1.5" data-testid="tab-import-jobs">
              <Upload className="h-4 w-4" />
              Import Jobs
            </TabsTrigger>
            <TabsTrigger value="email-recovery" className="gap-1.5" data-testid="tab-email-recovery">
              <Mail className="h-4 w-4" />
              Email Recovery
            </TabsTrigger>
          </TabsList>

          {/* ── Schema Migrations tab ── */}
          <TabsContent value="schema" data-testid="section-schema-migrations">
            <div className="flex items-center justify-end mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchMigrations()}
                disabled={migrationsFetching}
                data-testid="button-refresh-migrations"
              >
                {migrationsFetching ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>
            {migrationsLoading ? (
              <div className="flex items-center justify-center h-48">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : migrationsError || !migrationsData ? (
              <Card>
                <CardContent className="py-8 text-center text-sm text-muted-foreground">
                  Failed to load migration tracker. Try refreshing.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Repository files</CardTitle>
                    <CardDescription>
                      {migrationsData.repoFiles.length}{" "}
                      <span className="font-mono">.sql</span>{" "}
                      file{migrationsData.repoFiles.length === 1 ? "" : "s"} in{" "}
                      <span className="font-mono">migrations/</span> (excluding{" "}
                      <span className="font-mono">.down.sql</span>).
                    </CardDescription>
                  </CardHeader>
                </Card>
                <TrackerCard
                  title="Production"
                  description="Tracker the production migration runner writes to on boot."
                  tracker={migrationsData.production}
                  testidPrefix="production"
                />
                <TrackerCard
                  title="Test / Dev"
                  description="Tracker the vitest global setup writes to before the test suite runs."
                  tracker={migrationsData.test}
                  testidPrefix="test"
                />
              </div>
            )}
          </TabsContent>

          {/* ── Import Job History tab ── */}
          <TabsContent value="imports" data-testid="section-import-history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Import Job History
                </CardTitle>
                <CardDescription>{jobs?.length ?? 0} total jobs</CardDescription>
              </CardHeader>
              <CardContent>
                {jobsLoading ? (
                  <div className="flex items-center justify-center h-16">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : !jobs || jobs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No import jobs yet.{" "}
                    <Link href="/platform/ai-import" className="text-primary underline underline-offset-2">
                      Start one in AI Import
                    </Link>
                    .
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job ID</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Completed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.map((j) => (
                        <TableRow key={j.id} data-testid={`row-import-job-${j.id}`}>
                          <TableCell className="font-mono text-xs">{j.id.slice(0, 8)}…</TableCell>
                          <TableCell>
                            <div>{j.sourcePlatform}</div>
                            {j.sourceLabel && (
                              <div className="text-xs text-muted-foreground truncate max-w-[140px]">{j.sourceLabel}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[j.status] ?? "bg-slate-500"}>
                              {j.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{formatDate(j.createdAt)}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {j.committedAt
                              ? formatDate(j.committedAt)
                              : j.abortedAt
                              ? formatDate(j.abortedAt)
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Email Recovery tab ── */}
          <TabsContent value="email-recovery" data-testid="section-email-recovery">
            <EmailRecoveryWizard />
          </TabsContent>
        </Tabs>
      </div>
    </PlatformLayout>
  );
}
